'use client'
import { Box, Typography } from '@mui/material'
import React, { useCallback, useState } from 'react'

import CouponImage from '@/src/assets/svgs/CouponImage'
import SwipeableDrawerWithBackNavigation from '@/src/components/common/ui/swipeableDrawer'
import OfferDetailClient from '@/src/components/pages/offerDetail/OfferDetailClient'
import { getOfferSlugFromLink } from '@/src/utils/offerDetailUtils'

interface Coupon {
    id: string
    heading: string
    description: string
    targetLink: string
}

interface CouponSectionProps {
    coupons: Coupon[]
    isMobile?: boolean
}

/** Same pattern as AirlineFilter: cap strip width on md+ (3 chips ≈ column width; 4th+ scrolls). */
const COUPON_CARD_WIDTH_PX = 280
const COUPON_GAP_PX = 12
const VISIBLE_COUPON_SLOTS = 3.2
const COUPON_STRIP_MAX_WIDTH_PX =
    COUPON_CARD_WIDTH_PX * VISIBLE_COUPON_SLOTS + COUPON_GAP_PX * (VISIBLE_COUPON_SLOTS - 1)

const CouponSection: React.FC<CouponSectionProps> = ({ coupons, isMobile = false }) => {
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [activeOfferSlug, setActiveOfferSlug] = useState<string | null>(null)

    const handleCouponClick = useCallback((targetLink: string) => {
        if (isMobile) {
            const slug = getOfferSlugFromLink(targetLink)
            if (slug) {
                setActiveOfferSlug(slug)
                setDrawerOpen(true)
            }
        } else {
            window.open(targetLink, '_blank', 'noopener,noreferrer')
        }
    }, [isMobile])

    const handleDrawerClose = useCallback(() => {
        setDrawerOpen(false)
        setTimeout(() => setActiveOfferSlug(null), 300)
    }, [])

    if (!coupons || coupons.length === 0) {
        return null
    }

    const stripMaxWidth = isMobile
        ? { xs: '100%' as const }
        : { xs: '100%' as const, md: `${COUPON_STRIP_MAX_WIDTH_PX}px` }

    return (
        <>
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    pb: 1,
                    pt: { sm: 1, md: 0 }
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        maxWidth: stripMaxWidth,
                        width: '100%',
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: stripMaxWidth,
                            width: '100%',
                            minWidth: 0,
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            WebkitOverflowScrolling: 'touch',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1.5,
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            '&::-webkit-scrollbar': { display: 'none' },
                            boxSizing: 'border-box'
                        }}
                    >
                        {coupons.map(coupon => {
                    const hasLink = coupon.targetLink && coupon.targetLink.trim() !== ''

                    const couponContent = (
                        <>
                            <Box sx={{ mr: 1.5, flexShrink: 0 }}>
                                <CouponImage sx={{ fontSize: '20px' }} />
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: 'rgb(17, 166, 112)',
                                        lineHeight: 1.375
                                    }}
                                >
                                    {coupon.description}
                                </Typography>
                            </Box>
                        </>
                    )

                    const commonStyles = {
                        width: '280px',
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: '12px',
                        px: 1.5,
                        py: 1,
                        bgcolor: 'rgb(236, 248, 244)',
                        flexShrink: 0
                    }

                    return hasLink ? (
                        <Box
                            key={coupon.id}
                            onClick={() => handleCouponClick(coupon.targetLink)}
                            sx={{
                                ...commonStyles,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: 'rgb(226, 238, 234)',
                                    transform: 'translateY(-1px)'
                                }
                            }}
                        >
                            {couponContent}
                        </Box>
                    ) : (
                        <Box
                            key={coupon.id}
                            sx={commonStyles}
                        >
                            {couponContent}
                        </Box>
                    )
                        })}
                    </Box>
                </Box>
            </Box>

            {isMobile && (
                <SwipeableDrawerWithBackNavigation
                    open={drawerOpen}
                    onClose={handleDrawerClose}
                    sx={{ zIndex: 1400 }}
                >
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                        {activeOfferSlug && (
                            <OfferDetailClient offerSlug={activeOfferSlug} />
                        )}
                    </Box>
                </SwipeableDrawerWithBackNavigation>
            )}
        </>
    )
}

export { CouponSection }
export type { Coupon, CouponSectionProps }
